import { useEffect, useMemo, useState, type MouseEvent as ReactMouseEvent, type ReactNode } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  Popover,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import type { ConsolidatedEventCluster, SessionEvent, UserAssertion } from '@/types';
import { formatVideoTime, getJudgementLabel, getJudgementTextColor, type JudgementOption } from '@/types';
import { ComparedReviewedPart } from '@/components/review/ComparedReviewedPart';
import UserAvatar from '@/components/UserAvatar';
import DecisionEventEditPopover, {
  type DecisionEventEditValues,
} from '@/components/review/DecisionEventEditPopover';
import OfficialAnswersGrid from '@/components/review/OfficialAnswersGrid';
import {
  assertionLinkedEventId,
  clusterHasOfficialAnswer,
  consensusDistributions,
  formatClusterAgreementDisplayLabel,
  formatClusterOnFieldDecision,
  formatJudgementLabel,
  getClusterAnswerTableAssertions,
  getClusterOfficialAssertions,
  refreshClusterAfterEventEdit,
  reviewedBallLabel,
  reviewedDiffAgainstOfficials,
  reviewedSanctionLabel,
} from '@/components/review/consensus';
import {
  REVIEW_STATS_DIALOG_Z,
  reviewEditDialogZ,
  reviewEditMenuZ,
  reviewEditPopoverZ,
} from '@/components/review/reviewZIndex';

interface Props {
  open: boolean;
  cluster: ConsolidatedEventCluster | null;
  onClose: () => void;
  onJumpToTime?: (seconds: number, cluster: ConsolidatedEventCluster) => void;
  officialSourceUserId?: number;
  officialSourceUserIds?: number[];
  zIndex?: number;
  educationMode?: boolean;
  events?: SessionEvent[];
  canEditEvents?: boolean;
  onEditEvent?: (eventId: string, values: DecisionEventEditValues) => void | Promise<void>;
  videoDuration?: number;
  menuZIndex?: number;
}

type FilterOption = { value: string; label: string; count: number };

type DistributionRow = { label: string; count: number };

const DISTRIBUTION_BAR_GREEN = 'rgba(46, 125, 50, 0.5)';
const DISTRIBUTION_BAR_RED = 'rgba(198, 40, 40, 0.5)';
const DISTRIBUTION_BAR_GREY = 'rgba(158, 158, 158, 0.45)';

function distributionBarColorAny(
  label: string,
  officialLabels: Array<string | null | undefined>,
  answerRevealed: boolean
): string {
  const valid = officialLabels.filter((l): l is string => !!l);
  if (!answerRevealed || valid.length === 0) return DISTRIBUTION_BAR_GREY;
  return valid.includes(label) ? DISTRIBUTION_BAR_GREEN : DISTRIBUTION_BAR_RED;
}

function ReviewedDistributionList({
  title,
  rows,
  total,
  active,
  onToggle,
  keyPrefix,
  renderLabel,
  barColor,
}: {
  title: string;
  rows: DistributionRow[];
  total: number;
  active: Set<string>;
  onToggle: (label: string) => void;
  keyPrefix: string;
  renderLabel?: (label: string, active: boolean) => ReactNode;
  barColor?: (label: string) => string;
}) {
  if (rows.length === 0) {
    return (
      <Box>
        <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          —
        </Typography>
      </Box>
    );
  }
  return (
    <Box>
      <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
        {title}
      </Typography>
      <Stack spacing={1}>
        {rows.map((row) => {
          const pct = (row.count / total) * 100;
          const isActive = active.has(row.label);
          return (
            <Box
              key={`${keyPrefix}-${row.label}`}
              onClick={() => onToggle(row.label)}
              sx={{
                cursor: 'pointer',
                borderRadius: 1,
                px: 0.75,
                py: 0.25,
                bgcolor: isActive ? 'rgba(139,195,74,0.12)' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Stack direction="row" justifyContent="space-between" alignItems="center">
                {renderLabel ? (
                  renderLabel(row.label, isActive)
                ) : (
                  <Typography variant="body2" fontWeight={isActive ? 700 : 400}>
                    {row.label}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  {row.count} ({Math.round(pct)}%)
                </Typography>
              </Stack>
              <Box sx={{ mt: 0.25, height: 6, borderRadius: 4, bgcolor: 'action.hover', overflow: 'hidden' }}>
                <Box
                  sx={{
                    width: `${pct}%`,
                    height: '100%',
                    bgcolor: barColor ? barColor(row.label) : DISTRIBUTION_BAR_GREEN,
                  }}
                />
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
}

function metricColor(rate: number): string {
  if (rate >= 80) return '#2e7d32';
  if (rate >= 60) return '#546e7a';
  return '#a84300';
}

function judgementLabel(a: UserAssertion): string {
  if (a.judgement === 'correct' || a.judgement === 'incorrect' || a.judgement === 'uncertain') {
    return getJudgementLabel(a.judgement === 'incorrect' ? 'wrong' : a.judgement);
  }
  return formatJudgementLabel(a.judgement);
}

function judgementLabelToOption(label: string): JudgementOption | null {
  if (label === '正確') return 'correct';
  if (label === '錯誤') return 'wrong';
  if (label === '灰色') return 'uncertain';
  return null;
}

function ColoredJudgementText({ label, fontWeight = 400 }: { label: string; fontWeight?: number }) {
  const option = judgementLabelToOption(label);
  if (!option) {
    return (
      <Typography variant="body2" color="text.secondary" fontWeight={fontWeight}>
        {label}
      </Typography>
    );
  }
  return (
    <Typography variant="body2" component="span" sx={{ color: getJudgementTextColor(option), fontWeight: fontWeight || 700 }}>
      {label}
    </Typography>
  );
}

function toggleSetValue(set: Set<string>, value: string): Set<string> {
  const next = new Set(set);
  if (next.has(value)) next.delete(value);
  else next.add(value);
  return next;
}

function buildFilterOptions(items: UserAssertion[], valueFn: (a: UserAssertion) => string): FilterOption[] {
  const map = new Map<string, number>();
  for (const item of items) {
    const label = valueFn(item);
    map.set(label, (map.get(label) || 0) + 1);
  }
  return [...map.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([label, count]) => ({ value: label, label, count }));
}

function TableColumnFilterHeader({
  label,
  options,
  selected,
  onChange,
  popoverZIndex,
}: {
  label: string;
  options: FilterOption[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  popoverZIndex?: number;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const active = selected.size > 0;
  const filterable = options.length > 0;

  const handleOpen = (e: ReactMouseEvent<HTMLElement>) => {
    if (!filterable) return;
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };

  return (
    <>
      <TableCell sx={{ userSelect: 'none', whiteSpace: 'nowrap' }}>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography
            variant="body2"
            fontWeight={700}
            component="span"
            color={active ? 'secondary.main' : 'text.primary'}
          >
            {label}
          </Typography>
          {filterable && (
            <Box
              component="span"
              role="button"
              tabIndex={0}
              aria-label={`篩選${label}`}
              onClick={handleOpen}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleOpen(e as unknown as ReactMouseEvent<HTMLElement>);
                }
              }}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                cursor: 'pointer',
                borderRadius: 0.5,
                p: 0.25,
                color: active ? 'secondary.main' : 'text.secondary',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <FilterListIcon sx={{ fontSize: 16, display: 'block' }} />
            </Box>
          )}
          {active && (
            <Typography variant="caption" color="secondary.main" fontWeight={700}>
              ({selected.size})
            </Typography>
          )}
        </Stack>
      </TableCell>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        disableScrollLock
        slotProps={{
          root: popoverZIndex ? { sx: { zIndex: popoverZIndex } } : undefined,
          paper: { sx: { width: 280, maxHeight: 360 } },
        }}
      >
        <Box sx={{ p: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle2" fontWeight={800}>
              {label}
            </Typography>
            {active && (
              <Button size="small" onClick={() => onChange(new Set())}>
                清除
              </Button>
            )}
          </Stack>
          <Divider sx={{ mb: 1 }} />
          <Stack spacing={0} sx={{ maxHeight: 260, overflowY: 'auto' }}>
            {options.map((opt) => {
              const checked = selected.has(opt.value);
              return (
                <FormControlLabel
                  key={opt.value}
                  sx={{ mx: 0, py: 0.1, alignItems: 'flex-start' }}
                  control={
                    <Checkbox
                      size="small"
                      checked={checked}
                      onChange={() => onChange(toggleSetValue(selected, opt.value))}
                      sx={{ py: 0.5 }}
                    />
                  }
                  label={
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ width: '100%', pr: 0.5 }}>
                      {label === '評價' ? (
                        <ColoredJudgementText label={opt.label} />
                      ) : (
                        <Typography variant="body2">{opt.label}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary">
                        {opt.count}
                      </Typography>
                    </Stack>
                  }
                />
              );
            })}
          </Stack>
        </Box>
      </Popover>
    </>
  );
}

export default function EventConsensusDialog({
  open,
  cluster,
  onClose,
  onJumpToTime,
  officialSourceUserId,
  officialSourceUserIds,
  zIndex,
  educationMode = false,
  events = [],
  canEditEvents = false,
  onEditEvent,
  videoDuration,
  menuZIndex,
}: Props) {
  const [filterJudgement, setFilterJudgement] = useState<Set<string>>(() => new Set());
  const [filterReviewedBall, setFilterReviewedBall] = useState<Set<string>>(() => new Set());
  const [filterReviewedSanction, setFilterReviewedSanction] = useState<Set<string>>(() => new Set());
  const [revealOfficialAnswer, setRevealOfficialAnswer] = useState(false);
  const [editing, setEditing] = useState<{ event: SessionEvent; anchorEl: HTMLElement } | null>(null);
  const [displayCluster, setDisplayCluster] = useState<ConsolidatedEventCluster | null>(cluster);
  const [localEvents, setLocalEvents] = useState(events);
  const statsDialogZ = zIndex ?? REVIEW_STATS_DIALOG_Z;
  const filterPopoverZ = statsDialogZ + 1;
  const editPopoverZ = reviewEditPopoverZ(statsDialogZ);
  const editMenuZ = Math.max(menuZIndex ?? 0, reviewEditMenuZ(editPopoverZ));
  const editDialogZ = reviewEditDialogZ(editPopoverZ);
  const eventMap = useMemo(() => new Map(localEvents.map((e) => [e.id, e])), [localEvents]);

  useEffect(() => {
    setLocalEvents(events);
  }, [events]);

  useEffect(() => {
    setDisplayCluster(cluster);
  }, [cluster]);

  useEffect(() => {
    setFilterJudgement(new Set());
    setFilterReviewedBall(new Set());
    setFilterReviewedSanction(new Set());
    setRevealOfficialAnswer(false);
  }, [cluster?.clusterId, open]);

  const officialIds = useMemo(
    () =>
      officialSourceUserIds?.length
        ? officialSourceUserIds
        : officialSourceUserId != null
          ? [officialSourceUserId]
          : undefined,
    [officialSourceUserIds, officialSourceUserId]
  );

  const participantAssertions = useMemo(
    () => (displayCluster ? getClusterAnswerTableAssertions(displayCluster, officialIds) : []),
    [displayCluster, officialIds]
  );

  const filteredAssertions = useMemo(() => {
    return participantAssertions
      .filter((a) => {
        if (filterJudgement.size > 0 && !filterJudgement.has(judgementLabel(a))) return false;
        if (filterReviewedBall.size > 0 && !filterReviewedBall.has(reviewedBallLabel(a))) return false;
        if (filterReviewedSanction.size > 0 && !filterReviewedSanction.has(reviewedSanctionLabel(a)))
          return false;
        return true;
      })
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [participantAssertions, filterJudgement, filterReviewedBall, filterReviewedSanction]);

  const judgementFilterOptions = useMemo(
    () => buildFilterOptions(participantAssertions, judgementLabel),
    [participantAssertions]
  );

  const reviewedBallFilterOptions = useMemo(
    () => buildFilterOptions(participantAssertions, reviewedBallLabel),
    [participantAssertions]
  );

  const reviewedSanctionFilterOptions = useMemo(
    () => buildFilterOptions(participantAssertions, reviewedSanctionLabel),
    [participantAssertions]
  );

  if (!displayCluster) return null;

  const agreementRate = displayCluster.agreementRate;
  const colors = metricColor(agreementRate ?? 0);
  const distributions = consensusDistributions(displayCluster);
  const onFieldTitle = formatClusterOnFieldDecision(displayCluster);
  const officialAssertions = getClusterOfficialAssertions(displayCluster, officialIds);
  const hasOfficialReference = officialAssertions.length > 0;
  const agreementDisplayLabel = formatClusterAgreementDisplayLabel(displayCluster, officialIds);
  const total = Math.max(1, displayCluster.assertions.length);
  const hasActiveFilter =
    filterJudgement.size > 0 || filterReviewedBall.size > 0 || filterReviewedSanction.size > 0;
  const answerRevealed = !educationMode || revealOfficialAnswer;
  const hasOfficialAnswer = clusterHasOfficialAnswer(displayCluster, officialIds);
  const answerCardToggle = educationMode && hasOfficialAnswer;
  const highlightReviewedDiff = answerRevealed && hasOfficialAnswer;
  const officialJudgementLabels = officialAssertions.map((a) =>
    a.judgement && a.judgement !== 'pending' ? formatJudgementLabel(a.judgement) : null
  );
  const officialReviewedBalls = officialAssertions.map((a) => {
    const label = reviewedBallLabel(a);
    return label === '—' ? null : label;
  });
  const officialReviewedSanctions = officialAssertions.map((a) => reviewedSanctionLabel(a));

  const clearAllFilters = () => {
    setFilterJudgement(new Set());
    setFilterReviewedBall(new Set());
    setFilterReviewedSanction(new Set());
  };

  const canEditAssertion = (assertion: UserAssertion) => {
    if (!canEditEvents) return false;
    const eventId = assertionLinkedEventId(assertion);
    return !!(eventId && eventMap.has(eventId));
  };

  const openEdit = (e: ReactMouseEvent<HTMLButtonElement>, assertion: UserAssertion) => {
    const eventId = assertionLinkedEventId(assertion);
    if (!eventId) return;
    const event = eventMap.get(eventId);
    if (!event) return;
    const anchor = (e.currentTarget as HTMLElement).closest('td') as HTMLElement | null;
    setEditing({ event, anchorEl: anchor ?? e.currentTarget });
  };

  const tableColSpan = canEditEvents ? 7 : 6;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg" sx={zIndex ? { zIndex } : undefined}>
      <DecisionEventEditPopover
        key={editing?.event.id ?? 'consensus-closed'}
        open={!!editing}
        anchorEl={editing?.anchorEl ?? null}
        initial={editing?.event}
        showVideoTime
        videoDuration={videoDuration}
        popoverZIndex={editPopoverZ}
        menuZIndex={editMenuZ}
        dialogZIndex={editDialogZ}
        onClose={() => setEditing(null)}
        onSubmit={async (values) => {
          if (!editing) return;
          const eventId = editing.event.id;
          const prevCluster = displayCluster;
          const prevEvents = localEvents;
          if (displayCluster) {
            setDisplayCluster(
              refreshClusterAfterEventEdit(displayCluster, eventId, values, officialIds)
            );
          }
          setLocalEvents((prev) =>
            prev.map((e) =>
              e.id === eventId
                ? {
                    ...e,
                    video_second: values.video_second ?? e.video_second,
                    on_field_decision: values.on_field_decision ?? null,
                    on_field_punishment: values.on_field_punishment ?? null,
                    reviewed_decision: values.reviewed_decision ?? null,
                    reviewed_punishment: values.reviewed_punishment ?? null,
                    judgement: values.judgement ?? null,
                    note: values.note ?? null,
                  }
                : e
            )
          );
          setEditing(null);
          if (!onEditEvent) return;
          try {
            await onEditEvent(eventId, values);
          } catch {
            if (prevCluster) setDisplayCluster(prevCluster);
            setLocalEvents(prevEvents);
          }
        }}
      />
      <DialogTitle sx={{ p: 0 }}>
        <Box
          sx={{
            px: { xs: 2, sm: 2.5 },
            pt: 2,
            pb: 2.5,
            bgcolor: (t) => (t.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'),
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={2}
            alignItems={{ xs: 'stretch', sm: 'flex-start' }}
            justifyContent="space-between"
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                <Chip
                  label={formatVideoTime(displayCluster.anchorTimestamp)}
                  sx={{
                    height: 40,
                    mt: { xs: 0, sm: 1.5 },
                    px: 0.25,
                    fontWeight: 800,
                    fontSize: '1.25rem',
                    fontVariantNumeric: 'tabular-nums',
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    boxShadow: (t) =>
                      t.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(0,0,0,0.06)',
                    '& .MuiChip-label': { px: 1.25, lineHeight: 1.2 },
                  }}
                />
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="overline"
                    color="text.secondary"
                    sx={{ lineHeight: 1.2, letterSpacing: '0.08em', fontWeight: 600 }}
                  >
                    場上判決
                  </Typography>
                  <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.25, mt: 0.25 }}>
                    {onFieldTitle}
                  </Typography>
                </Box>
              </Stack>

              {officialAssertions.length > 0 && (
                <Stack
                  role={answerCardToggle ? 'button' : undefined}
                  tabIndex={answerCardToggle ? 0 : undefined}
                  aria-label={answerCardToggle ? (answerRevealed ? '隱藏答案' : '顯示答案') : undefined}
                  onClick={() => answerCardToggle && setRevealOfficialAnswer((v) => !v)}
                  onKeyDown={(e) => {
                    if (!answerCardToggle) return;
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setRevealOfficialAnswer((v) => !v);
                    }
                  }}
                  spacing={1.25}
                  sx={{
                    mb: 1.5,
                    p: 1.5,
                    borderRadius: 1.5,
                    bgcolor: 'background.paper',
                    borderStyle: answerCardToggle && !answerRevealed ? 'dashed' : 'solid',
                    borderWidth: answerCardToggle && !answerRevealed ? 2 : 1,
                    borderColor: answerCardToggle && !answerRevealed ? 'secondary.main' : 'divider',
                    cursor: answerCardToggle ? 'pointer' : 'default',
                    position: 'relative',
                    overflow: 'hidden',
                    transition: 'border-color 0.2s, background-color 0.2s',
                    '&:hover': answerCardToggle
                      ? {
                          bgcolor: 'action.hover',
                          borderColor: answerCardToggle && !answerRevealed ? 'secondary.dark' : 'divider',
                        }
                      : undefined,
                  }}
                >
                  <Box sx={{ visibility: answerRevealed ? 'visible' : 'hidden' }}>
                    <OfficialAnswersGrid assertions={officialAssertions} />
                  </Box>
                  {answerCardToggle && !answerRevealed && (
                    <Box
                      sx={{
                        position: 'absolute',
                        inset: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        borderRadius: 1.5,
                        bgcolor: (t) =>
                          t.palette.mode === 'dark' ? 'rgba(11,15,20,0.88)' : 'rgba(255,255,255,0.92)',
                      }}
                    >
                      <Typography variant="body2" color="text.secondary" fontWeight={700} letterSpacing="0.02em">
                        點擊顯示答案
                      </Typography>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>

            <Stack
              alignItems="center"
              spacing={1.25}
              sx={{
                flexShrink: 0,
                px: { xs: 0, sm: 1 },
                pt: { xs: 0.5, sm: 0 },
                minWidth: { sm: 108 },
              }}
            >
              <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                <CircularProgress
                  variant="determinate"
                  value={Math.max(0, Math.min(100, agreementRate ?? 0))}
                  size={64}
                  thickness={4}
                  sx={{
                    color: colors,
                    bgcolor: (t) =>
                      t.palette.mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    borderRadius: '50%',
                  }}
                />
                <Box
                  sx={{
                    inset: 0,
                    position: 'absolute',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 800, color: colors, lineHeight: 1.1 }}>
                    {agreementRate != null ? `${Math.round(agreementRate)}%` : '—'}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="caption" color="text.secondary" fontWeight={600}>
                {hasOfficialReference ? '一致率' : '共識率'}
              </Typography>
              <Button
                size="small"
                variant="contained"
                color="secondary"
                startIcon={<PlayArrowRoundedIcon />}
                onClick={() => {
                  const seconds = Math.max(0, displayCluster.anchorTimestamp - 0.8);
                  onJumpToTime?.(seconds, displayCluster);
                  onClose();
                }}
                sx={{ whiteSpace: 'nowrap', px: 1.75 }}
              >
                播放片段
              </Button>
            </Stack>
          </Stack>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 0 }}>
        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 3, pb: 2.5 }}>
          <Grid container spacing={2.5}>
            <Grid item xs={12} sm={6} md={3}>
              <ReviewedDistributionList
                title="覆核判決評價"
                rows={distributions.judgement}
                total={total}
                active={filterJudgement}
                onToggle={(label) => setFilterJudgement((s) => toggleSetValue(s, label))}
                keyPrefix="judgement"
                renderLabel={(label, active) => (
                  <ColoredJudgementText label={label} fontWeight={active ? 700 : 400} />
                )}
                barColor={(label) =>
                  distributionBarColorAny(label, officialJudgementLabels, answerRevealed)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReviewedDistributionList
                title="覆核球權分佈"
                rows={distributions.reviewedBall}
                total={total}
                active={filterReviewedBall}
                onToggle={(label) => setFilterReviewedBall((s) => toggleSetValue(s, label))}
                keyPrefix="reviewed-ball"
                barColor={(label) =>
                  distributionBarColorAny(label, officialReviewedBalls, answerRevealed)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <ReviewedDistributionList
                title="覆核罰則分佈"
                rows={distributions.reviewedSanction}
                total={total}
                active={filterReviewedSanction}
                onToggle={(label) => setFilterReviewedSanction((s) => toggleSetValue(s, label))}
                keyPrefix="reviewed-sanction"
                barColor={(label) =>
                  distributionBarColorAny(label, officialReviewedSanctions, answerRevealed)
                }
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Typography variant="body2" fontWeight={700} sx={{ mb: 0.75 }}>
                爭議摘要
              </Typography>
              <Stack spacing={0.75}>
                <Typography variant="body2">
                  {hasOfficialReference ? '一致%' : '共識%'}：{agreementDisplayLabel}
                  {hasOfficialReference
                    ? '（參與者 vs 官方，覆核判決評價、覆核球權、覆核罰則三項平均；多位官方取最佳吻合）'
                    : '（全部標記三項多数決一致比例之平均）'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  平均時間偏差 Δt：{(displayCluster.averageVariance || 0).toFixed(2)} 秒
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box sx={{ px: { xs: 2, sm: 3 }, pt: 2.5, pb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="body2" fontWeight={700}>
            作答
            <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
              {' '}
              · {participantAssertions.length} 則作答
            </Typography>
            {hasActiveFilter && (
              <Typography component="span" variant="body2" color="text.secondary" fontWeight={400}>
                {' '}
                · 顯示 {filteredAssertions.length} / {participantAssertions.length}
              </Typography>
            )}
          </Typography>
          {hasActiveFilter && (
            <Button size="small" onClick={clearAllFilters}>
              清除篩選
            </Button>
          )}
        </Stack>

        <Table size="small" sx={{ '& .MuiTableCell-root': { borderColor: 'divider' } }}>
          <TableHead>
            <TableRow>
              <TableCell>用戶</TableCell>
              <TableCell>時間</TableCell>
              <TableColumnFilterHeader
                label="覆核判決評價"
                options={judgementFilterOptions}
                selected={filterJudgement}
                onChange={setFilterJudgement}
                popoverZIndex={filterPopoverZ}
              />
              <TableColumnFilterHeader
                label="覆核球"
                options={reviewedBallFilterOptions}
                selected={filterReviewedBall}
                onChange={setFilterReviewedBall}
                popoverZIndex={filterPopoverZ}
              />
              <TableColumnFilterHeader
                label="覆核罰則"
                options={reviewedSanctionFilterOptions}
                selected={filterReviewedSanction}
                onChange={setFilterReviewedSanction}
                popoverZIndex={filterPopoverZ}
              />
              <TableCell>備註</TableCell>
              {canEditEvents && <TableCell padding="checkbox" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredAssertions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColSpan} sx={{ py: 3, textAlign: 'center', color: 'text.secondary' }}>
                  沒有符合篩選的作答
                </TableCell>
              </TableRow>
            ) : (
              filteredAssertions.map((a) => (
                <TableRow key={a.assertion_id} hover>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <UserAvatar
                        photoPath={a.photoPath}
                        displayName={a.userName}
                        sx={{ width: 24, height: 24, fontSize: 11 }}
                      />
                      <Typography variant="body2">{a.userName}</Typography>
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{formatVideoTime(a.timestamp)}</Typography>
                  </TableCell>
                  <TableCell>
                    <ColoredJudgementText label={judgementLabel(a)} />
                  </TableCell>
                  <TableCell>
                    <ComparedReviewedPart
                      label={reviewedBallLabel(a)}
                      highlight={
                        highlightReviewedDiff
                          ? reviewedDiffAgainstOfficials(a, officialAssertions, true).ball
                          : false
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <ComparedReviewedPart
                      label={reviewedSanctionLabel(a)}
                      highlight={
                        highlightReviewedDiff
                          ? reviewedDiffAgainstOfficials(a, officialAssertions, true).sanction
                          : false
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                      {a.notes?.trim() || '—'}
                    </Typography>
                  </TableCell>
                  {canEditEvents && (
                    <TableCell padding="checkbox" align="right">
                      {canEditAssertion(a) ? (
                        <Tooltip title="編輯標記">
                          <IconButton size="small" onClick={(e) => openEdit(e, a)} aria-label="編輯標記">
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : null}
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        </Box>
      </DialogContent>
    </Dialog>
  );
}
